/**
 * AdSense configuration surface for the public-website SPA.
 *
 * Real AdSense integration requires three operator-controlled steps
 * (per https://support.google.com/adsense/answer/9274516):
 *
 *   1. The site is approved by AdSense (review process — out-of-band).
 *   2. The publisher creates per-slot ad units in the AdSense dashboard
 *      and notes each one's `data-ad-slot` ID.
 *   3. The publisher's `data-ad-client` (`ca-pub-XXXXXXXXXXXXXXXX`) and
 *      the slot IDs are dropped into the SPA config.
 *
 * Until step 3 lands, this module returns an empty config and the
 * SPA's `<AdSlot>` component renders the podcast self-promotion
 * placeholder (which still carries the AdSense-policy-aware
 * "Sponsored" / "Advertisement" label and the matching shape per
 * the placement-policy guidance at
 * https://support.google.com/adsense/answer/1346295).
 *
 * **How the operator wires the real units (one-time):**
 *   1. Add `VITE_ADSENSE_CLIENT=ca-pub-XXXXXXXXXXXXXXXX` to the SPA's
 *      runtime config — for the deployed SPA this lands in
 *      `dist/config.json` via the deploy wrapper; for local dev it
 *      goes in `websites/public-website/.env.local`.
 *   2. Add the per-slot IDs to the same source:
 *        VITE_ADSENSE_SLOT_RELEASE_GRID=XXXXXXXXXX
 *        VITE_ADSENSE_SLOT_BETWEEN_SECTIONS_1=XXXXXXXXXX
 *        VITE_ADSENSE_SLOT_BETWEEN_SECTIONS_2=XXXXXXXXXX
 *   3. The bootstrap step in `main.tsx` writes these onto
 *      `globalThis.__DVGUP_CONFIG__.adsense` and the SPA's
 *      `<AdSenseScript>` component injects the page-level
 *      `<script src="...adsbygoogle.js?client=...">` once on
 *      first paint.
 *
 * No call-site changes downstream — the existing `<AdSlot variant="...">`
 * invocations switch to real ad units automatically once `enabled`
 * resolves true.
 */

import { getPublicWebsiteConfig } from '../api/config';

/**
 * Per-slot ID map. Each slot type maps to a single `data-ad-slot`
 * created in the AdSense dashboard.
 */
export interface AdSenseSlotIds {
  /** In-release-grid 16:9 slot (responsive display rectangle). */
  readonly releaseGrid?: string;
  /** First between-sections horizontal banner. */
  readonly betweenSections1?: string;
  /** Second between-sections horizontal banner. */
  readonly betweenSections2?: string;
}

/**
 * Resolved AdSense config — read at render time so the SPA picks up
 * config changes that arrive during bootstrap (after `/config.json`
 * is fetched in `main.tsx`).
 */
export interface AdSenseConfig {
  /**
   * `true` when a publisher client ID + at least one slot ID are
   * configured; the SPA's `<AdSlot>` then renders real
   * `<ins class="adsbygoogle">` units instead of the placeholder.
   */
  readonly enabled: boolean;
  /** `ca-pub-XXXXXXXXXXXXXXXX`. Empty string when AdSense is unconfigured. */
  readonly client: string;
  /** Per-slot IDs. Empty values are treated as "no slot configured". */
  readonly slots: AdSenseSlotIds;
}

/**
 * Inspect the runtime SPA config (populated by `main.tsx` from
 * `/config.json` at deploy time, or from `import.meta.env` in dev)
 * and return the resolved AdSense config. The function is pure /
 * referentially transparent within a single render — call it once
 * per `<AdSlot>` mount.
 */
export function getAdSenseConfig(): AdSenseConfig {
  const cfg = getPublicWebsiteConfig() as unknown as {
    adsense?: { client?: string; slots?: AdSenseSlotIds };
  };
  const fromBootstrap = cfg.adsense ?? {};
  const fromEnv = readFromEnv();
  const client = fromBootstrap.client ?? fromEnv.client ?? '';
  const slots: AdSenseSlotIds = {
    releaseGrid: fromBootstrap.slots?.releaseGrid ?? fromEnv.slots.releaseGrid,
    betweenSections1: fromBootstrap.slots?.betweenSections1 ?? fromEnv.slots.betweenSections1,
    betweenSections2: fromBootstrap.slots?.betweenSections2 ?? fromEnv.slots.betweenSections2,
  };
  const hasSlot = Boolean(
    slots.releaseGrid || slots.betweenSections1 || slots.betweenSections2,
  );
  return { enabled: client.length > 0 && hasSlot, client, slots };
}

/** Read AdSense overrides from Vite's compile-time env. Dev-only convenience. */
function readFromEnv(): { readonly client: string; readonly slots: AdSenseSlotIds } {
  const env = import.meta.env;
  return {
    client: typeof env.VITE_ADSENSE_CLIENT === 'string' ? env.VITE_ADSENSE_CLIENT : '',
    slots: {
      releaseGrid:
        typeof env.VITE_ADSENSE_SLOT_RELEASE_GRID === 'string'
          ? env.VITE_ADSENSE_SLOT_RELEASE_GRID
          : undefined,
      betweenSections1:
        typeof env.VITE_ADSENSE_SLOT_BETWEEN_SECTIONS_1 === 'string'
          ? env.VITE_ADSENSE_SLOT_BETWEEN_SECTIONS_1
          : undefined,
      betweenSections2:
        typeof env.VITE_ADSENSE_SLOT_BETWEEN_SECTIONS_2 === 'string'
          ? env.VITE_ADSENSE_SLOT_BETWEEN_SECTIONS_2
          : undefined,
    },
  };
}
